import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Download, X, Share, Smartphone } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface AddToHomeScreenButtonProps {
    variant?: 'default' | 'outline' | 'card' | 'image';
    className?: string;
}

export const AddToHomeScreenButton: React.FC<AddToHomeScreenButtonProps> = ({
    variant = 'default',
    className = '',
}) => {
    const { t } = useTranslation('mobile');
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [isInstalled, setIsInstalled] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [isAndroid, setIsAndroid] = useState(false);
    const [showIOSModal, setShowIOSModal] = useState(false);
    const [showDebug] = useState(false); // Set to true to debug Android issues

    // Static badge images from public directory
    // Replace these files in public/images/badges/ with your custom badges
    const androidBadge = '/images/badges/android-badge.svg';
    const iosBadge = '/images/badges/ios-badge.svg';

    useEffect(() => {
        // Check if app is already installed
        const checkInstalled = () => {
            // Check via display-mode media query
            if (window.matchMedia('(display-mode: standalone)').matches) {
                setIsInstalled(true);
                console.log('[A2HS] App is already installed (standalone mode)');
                return true;
            }

            // Check iOS standalone mode
            if ((navigator as any).standalone === true) {
                setIsInstalled(true);
                console.log('[A2HS] App is already installed (iOS standalone)');
                return true;
            }

            return false;
        };

        // Detect iOS
        const detectIOS = () => {
            const userAgent = window.navigator.userAgent.toLowerCase();
            return /iphone|ipad|ipod/.test(userAgent);
        };

        // Detect Android
        const detectAndroid = () => {
            const userAgent = window.navigator.userAgent.toLowerCase();
            return /android/.test(userAgent);
        };

        const isiOS = detectIOS();
        const isAndroidDevice = detectAndroid();

        setIsIOS(isiOS);
        setIsAndroid(isAndroidDevice);

        console.log('[A2HS] Platform detection:', { isiOS, isAndroidDevice });

        if (checkInstalled()) {
            return;
        }

        // Listen for beforeinstallprompt event (Android)
        const handleBeforeInstallPrompt = (e: Event) => {
            console.log('[A2HS] beforeinstallprompt event fired!');
            e.preventDefault();
            setDeferredPrompt(e as BeforeInstallPromptEvent);
        };

        // Listen for appinstalled event
        const handleAppInstalled = () => {
            console.log('[A2HS] App installed successfully!');
            setIsInstalled(true);
            setDeferredPrompt(null);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        // Log manifest and service worker status for debugging
        if (showDebug) {
            console.log('[A2HS] Debug Info:');
            console.log('- Service Worker supported:', 'serviceWorker' in navigator);
            console.log(
                '- Service Worker registered:',
                navigator.serviceWorker?.controller !== null
            );

            fetch('/api/v1/manifest.webmanifest')
                .then(res => {
                    console.log('- Manifest fetch status:', res.status, res.ok);
                    return res.json();
                })
                .then(manifest => console.log('- Manifest data:', manifest))
                .catch(err => console.error('- Manifest error:', err));
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, [showDebug]);

    const handleInstallClick = async () => {
        if (isIOS) {
            console.log('[A2HS] Showing iOS instructions');
            setShowIOSModal(true);
            return;
        }

        if (isAndroid && !deferredPrompt) {
            console.log('[A2HS] Android detected but no install prompt available yet');
            alert(t('installAndroidNoPrompt'));
            return;
        }

        if (!deferredPrompt) {
            console.log('[A2HS] No deferred prompt available');
            return;
        }

        // Show native Android install prompt
        try {
            console.log('[A2HS] Showing native install prompt');
            await deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;

            if (outcome === 'accepted') {
                console.log('[A2HS] User accepted A2HS prompt');
            } else {
                console.log('[A2HS] User dismissed A2HS prompt');
            }

            setDeferredPrompt(null);
        } catch (error) {
            console.error('[A2HS] Error showing install prompt:', error);
        }
    };

    // Don't show button if already installed
    if (isInstalled) {
        console.log('[A2HS] Button hidden - app already installed');
        return null;
    }

    // Show button on iOS or Android (even if prompt not available yet)
    if (!isIOS && !isAndroid) {
        console.log('[A2HS] Button hidden - not a mobile device');
        return null;
    }

    // Platform-specific icon components
    const AndroidIcon = () => (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85a.637.637 0 0 0-.83.22l-1.88 3.24a11.43 11.43 0 0 0-8.94 0L5.65 5.67a.643.643 0 0 0-.87-.2c-.28.18-.37.54-.22.83L6.4 9.48A10.81 10.81 0 0 0 1 18h22a10.81 10.81 0 0 0-5.4-8.52M7 15.25a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5m10 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5" />
        </svg>
    );

    const AppleIcon = () => (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01M12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25" />
        </svg>
    );

    // Render card variant for mobile profile
    if (variant === 'card') {
        return (
            <>
                <div
                    className={`bg-gradient-to-r from-blue-600 to-blue-500 rounded-xl p-4 shadow-lg ${className}`}
                >
                    <div className="flex items-start gap-3">
                        <div className="bg-white/20 rounded-lg p-2.5 flex-shrink-0 text-white">
                            {isAndroid ? (
                                <AndroidIcon />
                            ) : isIOS ? (
                                <AppleIcon />
                            ) : (
                                <Smartphone className="h-5 w-5" />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-white font-semibold text-sm mb-1">
                                {t('installApp')}
                            </h3>
                            <p className="text-white/90 text-xs mb-3">
                                {isAndroid
                                    ? t('installAppSubtitleAndroid')
                                    : t('installAppSubtitleIos')}
                            </p>
                            <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                onClick={handleInstallClick}
                                className="bg-white text-blue-600 hover:bg-white/90 font-medium"
                            >
                                {isAndroid ? (
                                    <AndroidIcon />
                                ) : isIOS ? (
                                    <AppleIcon />
                                ) : (
                                    <Download className="h-3.5 w-3.5" />
                                )}
                                <span className="ml-1.5">{t('installNow')}</span>
                            </Button>
                        </div>
                    </div>
                </div>

                {/* iOS Instructions Modal */}
                {showIOSModal && (
                    <IOSInstructionsModal onClose={() => setShowIOSModal(false)} t={t} />
                )}
            </>
        );
    }

    // Image variant - custom badge images
    if (variant === 'image') {
        const badgeUrl = isAndroid ? androidBadge : isIOS ? iosBadge : androidBadge;
        return (
            <>
                <button
                    type="button"
                    onClick={handleInstallClick}
                    className={`w-full flex items-center justify-center transition-all hover:scale-[1.02] hover:opacity-90 ${className}`}
                >
                    <img
                        src={badgeUrl}
                        alt={
                            isAndroid
                                ? t('getItOnAndroid')
                                : isIOS
                                  ? t('availableOnIphoneIpad')
                                  : t('addToHomeScreenAlt')
                        }
                        className="h-14 w-auto max-w-full object-contain"
                    />
                </button>

                {/* iOS Instructions Modal */}
                {showIOSModal && (
                    <IOSInstructionsModal onClose={() => setShowIOSModal(false)} t={t} />
                )}
            </>
        );
    }

    // Badge-style button for login page (store-like design)
    return (
        <>
            <button
                type="button"
                onClick={handleInstallClick}
                className={`w-full bg-black hover:bg-gray-900 text-white rounded-lg px-4 py-2.5 flex items-center justify-center gap-3 transition-all hover:scale-[1.02] shadow-md ${className}`}
            >
                {isAndroid ? (
                    <>
                        <AndroidIcon />
                        <div className="flex flex-col items-start text-left">
                            <span className="text-[10px] leading-tight opacity-90">GET IT ON</span>
                            <span className="text-sm font-semibold leading-tight">Android</span>
                        </div>
                    </>
                ) : isIOS ? (
                    <>
                        <AppleIcon />
                        <div className="flex flex-col items-start text-left">
                            <span className="text-[10px] leading-tight opacity-90">
                                Available on
                            </span>
                            <span className="text-sm font-semibold leading-tight">
                                iPhone & iPad
                            </span>
                        </div>
                    </>
                ) : (
                    <>
                        <Download className="h-4 w-4" />
                        <span>Add to Home Screen</span>
                    </>
                )}
            </button>

            {/* iOS Instructions Modal */}
            {showIOSModal && <IOSInstructionsModal onClose={() => setShowIOSModal(false)} t={t} />}
        </>
    );
};

// Separate iOS instructions modal component for cleaner, improved styling
const IOSInstructionsModal: React.FC<{
    onClose: () => void;
    t: (key: string) => string;
}> = ({ onClose, t }) => {
    return (
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-slate-900 rounded-2xl p-6 w-full max-w-sm border border-slate-200 dark:border-slate-700 shadow-2xl"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                        <div className="bg-blue-100 dark:bg-blue-900/30 rounded-full p-2">
                            <Smartphone className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                            {t('installAppModalTitle')}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 p-1"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="space-y-4 text-sm">
                    <p className="text-slate-600 dark:text-slate-300">{t('toInstallOnIphone')}</p>

                    <div className="space-y-3">
                        <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold mt-0.5">
                                1
                            </div>
                            <p className="flex-1 text-slate-700 dark:text-slate-300">
                                {t('installStep1Safari')}{' '}
                                <Share className="inline h-4 w-4 mx-1 align-text-bottom" />
                            </p>
                        </div>

                        <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold mt-0.5">
                                2
                            </div>
                            <p className="flex-1 text-slate-700 dark:text-slate-300">
                                {t('installStep2Scroll')}
                            </p>
                        </div>

                        <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold mt-0.5">
                                3
                            </div>
                            <p className="flex-1 text-slate-700 dark:text-slate-300">
                                {t('tapAddToConfirm') as string}
                            </p>
                        </div>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mt-4">
                        <p className="text-xs text-blue-800 dark:text-blue-300">
                            {t('appWillAppearOnHomeScreen')}
                        </p>
                    </div>
                </div>

                <Button onClick={onClose} className="w-full mt-6 bg-blue-600 hover:bg-blue-700">
                    {t('gotIt')}
                </Button>
            </div>
        </div>
    );
};
